import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardMedia, CardContent, Button, Stack, CardActions, Dialog, DialogContent, AppBar, Toolbar, IconButton, Snackbar, Alert } from '@mui/material';
import { associateImageWithProject, dissociateImageFromProject, getProjectImages } from '../../services/projects';
import { Image } from '../../types';
import { useLang } from '../../contexts/langContext';
import ImageManager from '../images/ImageManager';
import { Link as RouterLink } from 'react-router-dom';
import ProtectedImage from '../common/ProtectedImage';
import { Delete as DeleteIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useBranding } from '../../contexts/BrandingContext';
import { magicButtonProps } from '../common/ai';
import ImageGenerationDialog from '../images/ImageGenerationDialog';
import ImageUpload from '../images/ImageUpload';
import { useAuth } from '../../hooks/useAuth';

interface ProjectImageManagerProps {
  projectId: string;
}

function ProjectImageManager({ projectId }: ProjectImageManagerProps) {
  const { lang } = useLang();
  const { features } = useBranding();
  const enableImageGeneration = features?.enableImageGeneration ?? false;
  const [projectImages, setProjectImages] = useState<Image[]>([]);
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const { user } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  useEffect(() => {
    fetchProjectImages();
  }, [projectId]);

  const fetchProjectImages = async () => {
    const projectImgs = await getProjectImages(projectId);
    setProjectImages(projectImgs);
  };

  const handleAssociateImage = async (imageId: string) => {
    await associateImageWithProject(projectId, imageId, { scope: 'auto' });
    fetchProjectImages();
  };

  const handleProjectUploadSuccess = async (image?: Image) => {
    if (image) {
      await associateImageWithProject(projectId, image._id, { scope: 'auto' });
    }
    fetchProjectImages();
  };

  const handleGenerateSuccess = async ({ _id }: { _id: string }) => {
    await associateImageWithProject(projectId, _id, { scope: 'auto' });
    fetchProjectImages();
  };

  const handleDissociateImage = async (image: Image) => {
    await dissociateImageFromProject(projectId, image._id);
    fetchProjectImages();
    if (user && image.createdBy === user._id) {
      const message = lang === 'en'
        ? 'The image is not deleted and can still be found in the "My Images" tab of the image list page.'
        : '图片未被删除，仍可在图片列表页面的"我的图片"标签中找到。';
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    }
  };

  const handleOpenImageManager = () => {
    setIsImageManagerOpen(true);
  };

  const handleCloseImageManager = () => {
    setIsImageManagerOpen(false);
  };

  const handleSelectImage = async (imageId: string) => {
    await handleAssociateImage(imageId);
    handleCloseImageManager();
  };

  const renderToolbar = () => (
    <Stack direction="row" spacing={1} sx={{ my: 2 }}>
      <ImageUpload onUploadSuccess={handleProjectUploadSuccess} />
      {enableImageGeneration && (
        <Button
          onClick={() => setGenerationDialogOpen(true)}
          {...magicButtonProps({ loading: false })}
        >
          {lang === 'en' ? 'Generate Image' : '生成图片'}
        </Button>
      )}
      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenImageManager}>
        {lang === 'en' ? 'Add Image from Gallery' : '从图片库添加图片'}
      </Button>
    </Stack>
  );

  return (
    <Box>
      {projectImages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {lang === 'en' 
              ? 'No images associated with this project yet.' 
              : '该项目还没有关联的图片。'}
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 4 }} color="textSecondary">
            {lang === 'en'
              ? 'Add images to showcase your project visually.'
              : '添加图片以直观地展示您的项目。'}
          </Typography>
          {renderToolbar()}
          <Button
            component={RouterLink}
            to="/pictures"
            variant="text"
            color="primary"
          >
            {lang === 'en' ? 'View Image Gallery' : '查看图片库'}
          </Button>
        </Box>
      ) : (
        <>
          {renderToolbar()}
          <Grid container spacing={2}>
            {projectImages.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image._id}>
                <Card>
                  <CardMedia sx={{ height: 200 }}>
                    <ProtectedImage imageUrl={image.url} alt={image.alt} />
                  </CardMedia>
                  <CardContent>
                    <Typography variant="body2">{image.alt}</Typography>
                  </CardContent>
                  <CardActions>
                    <Button onClick={() => handleDissociateImage(image)} startIcon={<DeleteIcon />}>
                      {lang === 'en' ? 'Remove from Project' : '从项目中移除'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      
      <Dialog open={isImageManagerOpen} onClose={handleCloseImageManager} fullScreen>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseImageManager}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {lang === 'en' ? 'Select an Image' : '选择图片'}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          <ImageManager
            mode="picker"
            includePublic
            excludeImageIds={projectImages.map(img => img._id)}
            onImageSelect={handleSelectImage}
          />
        </DialogContent>
      </Dialog>

      {enableImageGeneration && (
        <ImageGenerationDialog
          open={generationDialogOpen}
          onClose={() => setGenerationDialogOpen(false)}
          onGenerateSuccess={handleGenerateSuccess}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ProjectImageManager;
